import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../App';
import MsMark from '../brand/MsMark';
import '../../styles/FloatingContact.css';

const PHONE = '+381669819240';
const EMAIL = 'info@mstechs.cloud';

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.1a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.7 2z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.9-.9L3 20.5l1.5-4.6A8.4 8.4 0 0 1 3.6 11.5 8.4 8.4 0 0 1 12 3.1h.5A8.4 8.4 0 0 1 21 11z" />
  </svg>
);

/**
 * Plutajuca precica do kontakta.
 *
 * Dizajn je nema — ovo je dodatak koji ostaje jer je na telefonu najkraci put
 * do poziva. Zato je i preveden na tokene: tamna povrsina, akcent samo na
 * glavnom dugmetu, MS znak umesto emojija. Ranije je nosio 15 zakucanih boja
 * (ljubicasta, narandzasta, zelena…) koje nisu iz palete.
 */
export default function FloatingContact() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getTranslation } = useContext(LanguageContext);
  const t = getTranslation('floatingContact');
  const wrapRef = useRef(null);
  const toggleRef = useRef(null);

  // Promena rute zatvara meni
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const close = useCallback(() => {
    setOpen(false);
    toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => { if (e.key === 'Escape') close(); };
    // Klik van menija ga zatvara. Slusa se `pointerdown` a ne `click` da se
    // meni zatvori pre nego sto se okine akcija ispod njega.
    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  const actions = [
    { key: 'contact', icon: <ChatIcon />, label: t.contact, onClick: () => navigate('/kontakt') },
    { key: 'email', icon: <MailIcon />, label: t.email, href: `mailto:${EMAIL}` },
    { key: 'phone', icon: <PhoneIcon />, label: t.phone, href: `tel:${PHONE}` },
  ];

  return (
    <div className={`fab${open ? ' is-open' : ''}`} ref={wrapRef}>
      <ul className="fab__menu" id="fab-menu" inert={!open}>
        {actions.map((a, i) => (
          <li key={a.key} className="fab__item" style={{ '--i': i }}>
            {a.href ? (
              <a className="fab__action" href={a.href}>
                <span className="fab__action-icon">{a.icon}</span>
                {a.label}
              </a>
            ) : (
              <button type="button" className="fab__action" onClick={a.onClick}>
                <span className="fab__action-icon">{a.icon}</span>
                {a.label}
              </button>
            )}
          </li>
        ))}
      </ul>

      <button
        ref={toggleRef}
        type="button"
        className="fab__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="fab-menu"
        aria-label={open ? 'Zatvori kontakt meni' : t.contact}
      >
        <span className="fab__toggle-mark" aria-hidden="true"><MsMark /></span>
        <span className="fab__toggle-close" aria-hidden="true">✕</span>
      </button>
    </div>
  );
}
